<template>
  <div>
    <div class="detail-filter-row">
      <div  v-for="filter in allFilters.filter(f => !f.isAction)"
            :key="filter.key"
            class="filter-item"
            @click="onShowFilterModal(filter)">
        <div class="filter-content">
          <div class="filter-label">
            <component  v-if="filter.icon"
                        :is="filter.icon"
                        :size="14"
                        class="filter-icon" />
            <span class="filter-text">{{ getFilterText(filter) }}</span>
          </div>
          <ChevronDown :size="14" class="filter-chevron" />
        </div>
      </div>

      <!-- Action Buttons -->
      <BButton  v-for="filter in allFilters.filter(f => f.isAction)"
                :key="filter.key"
                variant="primary"
                size="sm"
                :class="{ 'filter-action-icon-only': filter.iconOnly }"
                @click="onFilterAction(filter)">
        <component  v-if="filter.icon"
                    :is="filter.icon"
                    :size="14" />
        <span v-if="!filter.iconOnly" class="ms-1">{{ filter.label }}</span>
      </BButton>

      <FilterInfos  :filter-groups="getActiveFilters"
                    v-if="showFilterInfos"
                    @remove-tag="handleRemoveTag"
                    class="my-2" />

      <FilterModal  v-model="currentFilter.showModal"
                    :initial-data="currentFilter.options"
                    :filter-name="currentFilter.key"
                    :placeholder="currentFilter.placeholder"
                    @apply="onChangeModalFilter" />
    </div>
  </div>
</template>

<script>
import { Circle, Filter, ChevronDown, Globe,
  RefreshCw, MapPin, Layers, Funnel,
  SquareDashed, Square, Blocks, Building2,
  PackageOpen,Package, X } from 'lucide-vue-next';
import { mapGetters, mapActions } from 'vuex';
import FilterModal from '@/componentsv3/FilterModal.vue';
import FilterInfos from '@/componentsv3/FilterInfos.vue';
import { countCheckedLeaves } from '@/utils/treeHelpers';

export default {
  name: 'DetailFilterRow',

  components: {
    Filter,
    FilterInfos,
    FilterModal,
    ChevronDown,
    RefreshCw, Globe, MapPin, Layers, Funnel,
    SquareDashed, Square, Blocks, Building2, PackageOpen, Package, X
  },

  computed: {
    ...mapGetters('auth', [ 'clientId', 'isMSMUser' ]),
    ...mapGetters('clientData', [ 'filters' ]),
    ...mapGetters('localization', [ 'currentLocalization', 'currentLocale' ]),

    getActiveFilters() {
      let result = [];
      this.allFilters.filter(x => !x.isAction).forEach(filter => {
        let options = filter.customIndex !== -1 ? this.filters.customProperties[filter.customIndex] : this.filters[filter.key];
        const count = countCheckedLeaves(options);
        if (count > 0) {
          let tags = [];
          const gatherTags = (node) => {
            // Wenn node ein Array ist, iteriere darüber
            if (Array.isArray(node)) {
              node.forEach(item => gatherTags(item));
              return;
            }
            // Wenn node children hat, ist es kein Leaf - rekursiv durchgehen
            if (node.children && Array.isArray(node.children) && node.children.length > 0) {
              node.children.forEach(child => gatherTags(child));
            } else {
              // Kein children = Leaf node
              if (node.checked === true) {
                tags.push({
                  id: node.id || node.key || node.value,
                  label: node.label.replace('...', '').replace('…', '') || node.name.replace('...', '').replace('… ', '') || node.value,
                  icon: node.icon || null
                });
              }
            }
          };
          gatherTags(options);
          result.push({
            key: filter.key,
            label: filter.label.replace('...', '').replace('…', ''),
            tags: tags,
            divider: false
          });
        }
      });
      return result;
    }
  },

  data() {
    return {
      showFilterModal: false,
      allFilters: [],
      showFilterInfos: true,

      currentFilter: {
        showModal: false,
        options: [],
        key: null,
        placeholder: '',
      },

      defaultFilters: [
        { key: 'projects',  icon: Funnel,     placeholder: 'Webportal.StructureFilter.Placeholder' },
        { key: 'scenarios', icon: Layers,     placeholder: 'Webportal.ScenarioFilter.Placeholder' },
        { key: 'waves',     icon: Globe,      placeholder: 'Webportal.WaveFilter.Placeholder' },
        { key: 'region',    icon: Square,     placeholder: 'Webportal.RegionFilter.Placeholder' },
        { key: 'outlet',    icon: MapPin,     placeholder: 'Webportal.ClientLocationFilter.Placeholder' },
        { key: 'locations', icon: MapPin,     placeholder: 'Webportal.LocationFilter.Placeholder' },
        { key: 'refresh',   icon: RefreshCw,  label: this.$t('Webportal.Refresh'), isAction: true },
        { key: 'reset',     icon: X,          isAction: true, iconOnly: true },
        { key: 'fold',      icon: ChevronDown,isAction: true, iconOnly: true }
      ],

      defaultFiltersBMW: [
        { key: 'Brand',           icon: Funnel,       placeholder: 'Webportal.BrandFilter.Placeholder' },
        { key: 'Surveys',         icon: Layers,       placeholder: 'Webportal.SurveyFilter.Placeholder' },
        { key: 'Waves',           icon: Globe,        placeholder: 'Webportal.WaveFilter.Placeholder' },
        { key: 'Regions',         icon: Square,       placeholder: 'Webportal.RegionFilter.Placeholder' },
        { key: 'Subregions',      icon: Blocks,       placeholder: 'Webportal.SubregionFilter.Placeholder' },
        { key: 'NSCs',            icon: Package,      placeholder: 'Webportal.DashboardBMW.NSCFilter.Placeholder' },
        { key: 'SubNSCs',         icon: PackageOpen,  placeholder: 'Webportal.DashboardBMW.NSCSubregionFilter.Placeholder' },
        { key: 'Areas',           icon: SquareDashed, placeholder: 'Webportal.AreaFilter.Placeholder' },
        { key: 'Dealergroups',    icon: Building2,    placeholder: 'Webportal.DashboardBMW.DealergroupFilter.Placeholder' },
        { key: 'ClientLocations', icon: MapPin,       placeholder: 'Webportal.DashboardBMW.OutletFilter.Placeholder' },
        { key: 'WebsiteAnswers',  icon: Globe,        placeholder: 'Webportal.DashboardBMW.ContactInitiationFilter.Placeholder' },
        { key: 'refresh',         icon: RefreshCw,    label: this.$t('Webportal.Refresh'), isAction: true },
        { key: 'reset',           icon: X,            isAction: true, iconOnly: true },
        { key: 'fold',            icon: ChevronDown,  isAction: true, iconOnly: true }
      ],

      activeFilters: [
        {
          key: 'brand',
          label: 'Brand',
          tags: [
            { id: 1, label: 'BMW - BMW' },
            { id: 2, label: 'BMW - GKL' },
            { id: 3, label: 'BMW - xEV (BMW i)' },
            { id: 4, label: 'BMW - xEV (BMW i)/GKL' },
            { id: 5, label: 'BMW - xEV (PHEV)' },
            { id: 6, label: 'BMW - xEV (PHEV)/GKL' }
          ],
          divider: false
        },
        {
          key: 'scenario',
          label: 'Scenario',
          tags: [
            { id: 1, label: 'All Scenarios (9)' }
          ]
        },
        {
          key: 'wave',
          label: 'Wave',
          tags: [
            { id: 1, label: '2024_Q4', icon: Circle },
            { id: 2, label: '2025_T1', icon: Circle },
            { id: 3, label: '2025_T2', icon: Circle },
            { id: 4, label: '2025_T3', icon: Circle }
          ]
        }
      ],
    };
  },

  mounted() {
    if (this.filters) {
      this.allFilters = this.createFilterSettings(this.filters);
    }
  },

  watch: {
    filters: {
      handler(newFilters) {
        if (newFilters) {
          this.allFilters = this.createFilterSettings(newFilters);
        }
      },
      immediate: true, // Wird sofort beim Mount ausgeführt
      deep: true
    }
  },

  emits: ['filter-changed', 'filter-click'],

  methods: {
    raiseRefreshData() {
      this.$emit('filter-changed', this.currentFilter, this.filters);
    },

    handleRemoveTag({ groupKey, tag }) {
      log.info('Remove tag:', groupKey, tag.label);
      // TODO:
    },

    onFilterAction(filter) {
      switch (filter.key) {
        case 'refresh':
          this.raiseRefreshData();
          break;
        case 'fold':
          this.showFilterInfos = !this.showFilterInfos;
          break;
        case 'reset':
          // Reset all filters
          Object.keys(this.filters).forEach(key => {
            if (Array.isArray(this.filters[key])) {
              const resetArray = (arr) => {
                arr.forEach(item => {
                  if (item.children && Array.isArray(item.children)) {
                    resetArray(item.children);
                  } else {
                    item.checked = false;
                  }
                });
              };
              resetArray(this.filters[key]);
            }
          });
          this.raiseRefreshData();
          break;
        default:
          log.warn('Unknown filter action:', filter.key);
          break;
      }
    },

    onChangeModalFilter(name, items, selection) {
      this.filters[name] = items;

      // TODO: Automatically refresh data or only on user action?
      // this.raiseRefreshData();
    },

    onShowFilterModal(filter) {
      let options = filter.customIndex !== -1 ? this.filters.customProperties[filter.customIndex] : this.filters[filter.key];
      this.currentFilter = {
        options: Array.isArray(options) ? options : [options],
        key: filter.key,
        placeholder: filter.label,
        showModal: true
      };
      // log.warn("Modal w/ FLTR: ", this.currentFilter);
    },

    getFilterText(filter) {
      const count = countCheckedLeaves(this.filters[filter.key]);
      if (count === 0)
        return `${filter.label}`;
      else
        return `${filter.label} (${count})`;
    },

    createFilterSettings(rawFilters) {
      const settings = [];
      if (rawFilters === null || rawFilters === undefined) return settings;

      // TODO: bmw filters[0].brand ... normal: filters.brand
      let filters = _.isArray(rawFilters) && rawFilters.length === 1 ? rawFilters[0] : rawFilters;

      const dfltFilters = APP_TARGET === 'bmw' ? this.defaultFiltersBMW : this.defaultFilters;
      dfltFilters.forEach(dflt => {
        if (Object.hasOwn(filters, dflt.key)) {
          const filter = filters[dflt.key];
          settings.push({
            key: dflt.key,
            customIndex: -1,
            label: this.$t(dflt.placeholder), // filter.selectedOption ? filter.selectedOption.label : filter.label,
            icon: dflt.icon || filter.icon || null,
            isAction: filter.isAction || false
          });
          return;
        }

        filters.customProperties?.forEach((item) => {
          if (dflt.key === item.label.toLowerCase()) {
            // console.warn("HIT", dflt.key, item);
            settings.push({
              key: dflt.key,
              customIndex: filters.customProperties.indexOf(item),
              label: dflt.placeholder ? this.$t(dflt.placeholder) : item.label,
              icon: dflt.icon || filter.icon || null,
              isAction: item.isAction || false
            });
          }
        });
      });
      settings.push(...dfltFilters.filter(x => x.isAction));
      return settings;
    },
  }
};
</script>

<style lang="scss" scoped>
.detail-filter-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
  width: 100%;
}

.filter-item {
  background: var(--bs-secondary-bg);
  border-color: var(--bs-secondary-bg) !important;
  position: relative;
  border-radius: var(--radius-md);
  height: 1.925rem;
  min-width: 180px;
  flex-grow: 1;
  flex-shrink: 0;
  cursor: pointer;
  transition: all 0.15s;

  &:hover {
    background: var(--bs-tertiary-bg);
    border-color: var(--bs-tertiary-bg) !important;
  }
}

.filter-content {
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 12px;
  height: 100%;
  position: relative;
  z-index: 10;
}

.filter-label {
  display: flex;
  align-items: center;
  gap: 8px;
  overflow: hidden;
}

.filter-icon {
  flex-shrink: 0;
}

.filter-chevron {
  flex-shrink: 0;
}

.filter-text {
  font-size: 0.75rem;
  color: var(--bs-body-color);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

// Action Button Styles
.filter-action-icon-only {
  min-width: 2rem !important;
  width: 2rem;
  padding: 0.25rem !important;
  display: flex;
  gap: 8px;
  align-items: center;
  justify-content: center;
}

// BButton im Filter-Row Context
.detail-filter-row .btn {
  height: 1.925rem;
  display: flow;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
}
</style>
